import { t } from 'i18next';
import {
  Button,
  ButtonArrowBottom,
  ButtonArrowTop,
  ButtonDay,
  ContentDate,
  Container, ContainerButtons, ContainerCardDays, ContainerInput, ContainerInputs, DateLabel, InputStyledContainer, InputStyleHour, WeekDayButton,
  ButtonRepeatYearly,
  ContainerExceptionsView,
} from './styles';
import { useStateVar } from '~/helpers/useStateVar';
import {
  Dispatch, SetStateAction, useEffect, useMemo, useState,
} from 'react';
import { colors } from '~/styles/colors';
import { Trash3Icon } from '~/icons';
import { toast } from 'react-toastify';
import { apiCall } from '~/providers';
import { CaretDownFilled, CaretUpFilled, PlusCircleOutlined } from '@ant-design/icons';
import { Checkbox, Input } from '~/components';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment-timezone';
import { StyledCalendarIcon } from '../styles';
import { NewPagination } from '~/components/NewPagination';

export type TListHour = {
  openHour: string,
  closeHour: string,
  selectedDays: {
    sun: boolean,
    mon: boolean,
    tue: boolean,
    wed: boolean,
    thu: boolean,
    fri: boolean,
    sat: boolean,
  },
  type?: string,
}

export type TExceptions = {
  type?: string;
  id?: number;
  day: string;
  openHour: string;
  closeHour: string;
  repeat_every_year: boolean;
  name: string;
}
const ITEMS_PER_PAGE = 3;

export function EditOperatingHours({
  isPanel = false, listOperatingHoursUnit, setUnitHour, setDeleteUnitHour, unitId, isAddUnit = false, listExceptionsUnit, setUnitHourException, setDeleteUnitHourException,
}: Readonly<{
  isPanel?: boolean, listOperatingHoursUnit: TListHour[], setUnitHour: Dispatch<SetStateAction<TListHour[]>>, setDeleteUnitHour: Dispatch<SetStateAction<TListHour[]>>, unitId: number | null, isAddUnit?: boolean, listExceptionsUnit: TExceptions[], setUnitHourException: Dispatch<SetStateAction<TExceptions[]>>, setDeleteUnitHourException: Dispatch<SetStateAction<TExceptions[]>>
}>) {
  const [state, render, setState] = useStateVar(() => ({
    listHours: listOperatingHoursUnit,
    listExceptions: listExceptionsUnit,
    deleteExceptions: [] as TExceptions[],
    deleteList: [] as TListHour[],
    selectedDays: {},
    loading: false,
    sheet: 'hours' as 'hours' | 'exceptions',
    blockedDates: [] as string[],
    searchDate: null as string | null,
  }));
  const [focus, setFocus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil((state.listExceptions || []).length / ITEMS_PER_PAGE);
  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  async function getOperatingHoursUnit() {
    try {
      if (unitId) {
        const { listHours, listExceptions } = await apiCall('/unit/get-operating-hours', { unitId });
        setState({ listHours, listExceptions, blockedDates: listExceptions.map((ex) => ex.day) });
        if (!listHours.length) {
          setState({
            listHours: [{
              openHour: '00:00',
              closeHour: '23:59',
              selectedDays: {
                sun: false,
                mon: false,
                tue: false,
                wed: false,
                thu: false,
                fri: false,
                sat: false,
              },
              type: 'add',
            }],
          });
        }
        if (!listExceptions.length) {
          setState({
            listExceptions: [{
              name: '',
              day: '',
              type: 'add',
              openHour: '00:00',
              closeHour: '23:59',
              repeat_every_year: false,
            }],
          });
        }
      }
      else if (isAddUnit) {
        setState({
          listHours: [{
            openHour: '00:00',
            closeHour: '23:59',
            selectedDays: {
              sun: false,
              mon: false,
              tue: false,
              wed: false,
              thu: false,
              fri: false,
              sat: false,
            },
            type: 'add',
          }],
          listExceptions: [{
            name: '',
            day: '',
            type: 'add',
            openHour: '00:00',
            closeHour: '23:59',
            repeat_every_year: false,
          }],
        });
      }
    } catch (err) {
      toast.error(t('erroAoBuscarHorariosUnidade'));
      console.log(err);
    }
  }

  function decideData() {
    return state.searchDate ? state.listExceptions.filter((item) => item.day === state.searchDate) : state.listExceptions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }
  const dataPerPage = useMemo(() => decideData(), [state.listExceptions, currentPage, state.searchDate]);

  useEffect(() => {
    getOperatingHoursUnit();
  }, []);

  useEffect(() => {
    setUnitHour(state.listHours);
    setDeleteUnitHour(state.deleteList);
  }, [state.listHours, state.deleteList]);

  useEffect(() => {
    setUnitHourException(state.listExceptions);
    setDeleteUnitHourException(state.deleteExceptions);
    setState({ blockedDates: state.listExceptions.filter((ex) => ex.type !== 'delete').map((ex) => ex.day) });
  }, [state.listExceptions, state.deleteExceptions]);

  function addNewTime() {
    const previewTime = state.listHours;
    previewTime.push({
      openHour: '00:00',
      closeHour: '23:59',
      selectedDays: {
        sun: false,
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
      },
      type: 'add',
    });
    state.listHours = previewTime;
    setState({ listHours: previewTime });
  }

  function addNewException() {
    const previewTime = [{
      openHour: '00:00',
      closeHour: '23:59',
      day: '',
      name: '',
      repeat_every_year: false,
      type: 'add',
    }, ...state.listExceptions];
    state.listExceptions = previewTime;
    setState({ blockedDates: state.listExceptions.filter((ex) => ex.type !== 'delete').map((ex) => ex.day) });
    setState({ listExceptions: previewTime, searchDate: null });
    setCurrentPage(1);
  }

  function deleteException(index: number) {
    state.listExceptions[index].type = 'delete';
    const filterList = state.listExceptions.filter((item) => item.type !== 'delete');
    state.deleteExceptions = [...state.deleteExceptions, state.listExceptions[index]];
    state.listExceptions = filterList;
    if (!filterList.length) {
      addNewException();
    }
    render();
  }

  function deleteTime(index) {
    state.listHours[index].type = 'delete';
    const filterList = state.listHours.filter((item) => item.type !== 'delete');
    state.deleteList = [...state.deleteList, state.listHours[index]];
    state.listHours = filterList;
    if (!filterList.length) {
      addNewTime();
    }
    render();
  }

  function returnButton(type: 'hours' | 'exceptions') {
    setState({ sheet: type, searchDate: null });
    render();
  }

  return (
    <Container>
      <h1>{t('horarioDeFuncionamento')}</h1>
      <ContainerButtons>
        <ButtonDay selected={state.sheet === 'hours'} onClick={() => returnButton('hours')} type="button">{t('horarios')}</ButtonDay>
        <ButtonDay selected={state.sheet === 'exceptions'} onClick={() => returnButton('exceptions')} type="button">{t('excecoes')}</ButtonDay>
      </ContainerButtons>
      {
        state.sheet === 'exceptions' && (
          <ContainerExceptionsView>
            {
              state.listExceptions.every((item) => item.name.length > 0) && (
                <Button className="addButton" style={{ marginTop: 15 }} onClick={() => addNewException()}>
                  <PlusCircleOutlined onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
                  <span>{t('adicionarNovaExcecao')}</span>
                </Button>
              )
            }
            <div>
              <ContentDate style={{ width: 300 }}>
                <DateLabel>{t('pesquisar')}</DateLabel>
                <SingleDatePicker
                  numberOfMonths={1}
                  focused={focus}
                  onFocusChange={({ focused }) => setFocus(focused)}
                  date={state.searchDate ? moment(state.searchDate, 'YYYY-MM-DD') : null}
                  onDateChange={(date) => { state.searchDate = date ? date.format('YYYY-MM-DD') : ''; render(); }}
                  displayFormat="DD/MM/YYYY"
                  placeholder={t('selecioneUmaData')}
                  isOutsideRange={() => false}
                />
                <StyledCalendarIcon color={colors.Blue300} />
              </ContentDate>
              { state.searchDate && <span style={{ color: colors.Blue300, cursor: 'pointer' }} onClick={() => setState({ searchDate: null })}>{t('limparCampo')}</span>}
            </div>
            {
              (dataPerPage || []).filter((item) => item.type !== 'delete')?.map((item, index) => (
                <CardExceptions key={item.openHour + item.closeHour + index} values={item} render={render} isPanel={isPanel} index={index} deleteTime={deleteException} blockedDates={state.blockedDates} lengthArray={state.listExceptions.length} page={currentPage} />
              ))
            }
            {
              state.listExceptions.length > 3 && (
                <NewPagination
                  currentPage={currentPage}
                  onPageChange={handleChangePage}
                  totalPages={totalPages}
                />
              )
            }
          </ContainerExceptionsView>
        )
      }
      {
        state.sheet === 'hours' && (
          <>
            {
              state.listHours?.filter((item) => item.type !== 'delete').map((item, index) => (
                <CardDaysHours key={item.openHour + item.closeHour + index} values={item} render={render} isPanel={isPanel} index={index} deleteTime={deleteTime} />
              ))
            }
            {
              hasAnyDaySelected(state.listHours, false) && (
                <Button className="addButton" style={{ marginTop: 15 }} onClick={() => addNewTime()}>
                  <PlusCircleOutlined onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
                  <span>{t('adicionarNovoHorario')}</span>
                </Button>
              )
            }
          </>
        )
      }
    </Container>
  );
}

function hasAnyDaySelected(listHours: TListHour[], isException: boolean): boolean {
  if (isException) return true;
  return listHours.some((item) => Object.values(item.selectedDays).some((day) => day === true));
}

function CardDaysHours({
  values, render, isPanel, index, deleteTime, isException = false,
}) {
  if (values.type === 'delete') {
    return (
      <></>
    );
  }

  const days = [
    { key: 'sun', label: 'dom' },
    { key: 'mon', label: 'seg' },
    { key: 'tue', label: 'ter' },
    { key: 'wed', label: 'qua' },
    { key: 'thu', label: 'qui' },
    { key: 'fri', label: 'sex' },
    { key: 'sat', label: 'sab' },
  ] as const;

  return (
    <ContainerCardDays style={{ backgroundColor: isPanel ? colors.bg['weak-100'] : 'unset', padding: isPanel ? 10 : 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h4>{`${t('horario')} ${index + 1}`}</h4>
        {
          ((index !== 0 || hasAnyDaySelected([values], isException)) && isPanel) && (
            <div onClick={() => deleteTime(index)}>
              <Trash3Icon size="24" color={colors.RedDark} />
            </div>
          )
        }
      </div>
      <span>{t('selecioneOsDias')}</span>
      <div style={{ display: 'flex', paddingTop: '10px', fontSize: '90%' }}>
        {days.map(({ key, label }) => (
          <WeekDayButton
            key={key}
            checked={values.selectedDays[key]}
            onClick={() => {
              values.selectedDays[key] = !values.selectedDays[key];
              render();
            }}
          >
            {t(`diasDaSemana.${label}`).toUpperCase()}
          </WeekDayButton>
        ))}
      </div>
      <ComponentSelectHours
        render={render}
        values={values}
        isPanel={isPanel}
        index={index}
        deleteTime={deleteTime}
        margin={10}
      />
    </ContainerCardDays>
  );
}

function ComponentSelectHours({
  render, values, isPanel, index, deleteTime, isException = false, margin = 20,
}) {
  function changeHour(type: 'add' | 'sub', value: string) {
    const [hour, minute] = value.split(':').map(Number);
    const newHour = type === 'add' ? (hour + 1) % 24 : (hour - 1 + 24) % 24;
    return `${String(newHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }
  return (
    <ContainerInputs marginBottom={margin}>
      <InputStyledContainer>
        <ButtonArrowTop
          style={{
            right: '10px', bottom: '28px', padding: 0, height: 14,
          }}
          onClick={() => {
            const newHour = changeHour('add', values.openHour);
            values.openHour = newHour;
            render();
          }}
        >
          <CaretUpFilled onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
        </ButtonArrowTop>
        <label htmlFor="open-time">
          {t('horarioAbertura')}
        </label>
        <InputStyleHour
          id="open-time"
          type="time"
          value={values.openHour}
          onChange={(e) => { values.openHour = e.target.value; render(); }}
        />
        <ButtonArrowBottom
          style={{
            right: '10px', bottom: '12px', padding: 0, height: 14,
          }}
          onClick={() => {
            const newHour = changeHour('sub', values.openHour);
            values.openHour = newHour;
            render();
          }}
        >
          <CaretDownFilled onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
        </ButtonArrowBottom>
      </InputStyledContainer>
      <InputStyledContainer>
        <ButtonArrowTop
          style={{
            right: '10px', bottom: '28px', padding: 0, height: 14,
          }}
          onClick={() => {
            const newHour = changeHour('add', values.closeHour);
            values.closeHour = newHour;
            render();
          }}
        >
          <CaretUpFilled onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
        </ButtonArrowTop>
        <label htmlFor="close-time">
          {t('horarioFechamento')}
        </label>
        <InputStyleHour
          id="close-time"
          type="time"
          value={values.closeHour}
          onChange={(e) => { values.closeHour = e.target.value; render(); }}
        />
        <ButtonArrowBottom
          style={{
            right: '10px', bottom: '12px', padding: 0, height: 14,
          }}
          onClick={() => {
            const newHour = changeHour('sub', values.closeHour);
            values.closeHour = newHour;
            render();
          }}
        >
          <CaretDownFilled onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
        </ButtonArrowBottom>
      </InputStyledContainer>
      {
        ((index !== 0 || hasAnyDaySelected([values], isException)) && !isPanel) && (
          <Button onClick={() => deleteTime(index)} style={{ color: colors.RedDark, borderColor: colors.RedDark }}>
            <Trash3Icon color={colors.RedDark} />
            {t('apagarHorario')}
          </Button>
        )
      }
    </ContainerInputs>
  );
}

function CardExceptions({
  values, render, isPanel, index, deleteTime, blockedDates, lengthArray, page,
}) {
  const [focus, setFocus] = useState(false);
  return (
    <ContainerCardDays style={{ backgroundColor: isPanel ? colors.bg['weak-100'] : 'unset', padding: isPanel ? 10 : 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h4>{values.type === 'add' ? t('excecao') : `${t('excecao')} ${index + 1 + (page - 1) * 3}` }</h4>
        {
          ((index !== 0 || hasAnyDaySelected([values], true)) && isPanel) && (
            <div onClick={() => deleteTime(index)}>
              <Trash3Icon size="24" color={colors.RedDark} />
            </div>
          )
        }
      </div>
      <ContainerInput>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Input
            style={{
              margin: '5x 0px 5px 0px',
              height: '50px',
              width: '300px',
              fontSize: '12px',
              border: `1px solid ${colors.Grey200}`,
            }}
            type="text"
            value={values.name}
            placeholder={t('digite')}
            label={t('nomeExcecao')}
            onChange={(event) => { values.name = event.target.value; render(); }}
            data-test-id="nomeExcecao"
          />
          <ContentDate>
            <DateLabel>{t('dataExcecao')}</DateLabel>
            <SingleDatePicker
              numberOfMonths={1}
              focused={focus}
              onFocusChange={({ focused }) => setFocus(focused)}
              date={values.day ? moment(values.day, 'YYYY-MM-DD') : null}
              onDateChange={(date) => { values.day = date ? date.format('YYYY-MM-DD') : ''; render(); }}
              displayFormat="DD/MM/YYYY"
              placeholder={t('selecioneUmaData')}
              isOutsideRange={() => false}
              isDayBlocked={(day) => blockedDates.includes(day.format('YYYY-MM-DD'))}
            />
            <StyledCalendarIcon color={colors.Blue300} />
          </ContentDate>
        </div>
        <ComponentSelectHours
          render={render}
          values={values}
          isPanel={isPanel}
          index={index}
          deleteTime={deleteTime}
          isException
          margin={10}
        />
      </ContainerInput>
      <ButtonRepeatYearly>
        <Checkbox checked={values.repeat_every_year} onClick={(e) => { values.repeat_every_year = !values.repeat_every_year; render(); }} />
        <p style={{ margin: 0 }}>{t('repetirAnualmente')}</p>
      </ButtonRepeatYearly>
    </ContainerCardDays>
  );
}

export function validateOperatingHours(list: TListHour[]): string | null {
  for (const h of list) {
    const open = parseHour(h.openHour);
    const close = parseHour(h.closeHour);

    if (close <= open) {
      return `${t('horarioInvalido')}: ${h.openHour} - ${h.closeHour} (${t('fechamentoAntesAbertura')})`;
    }
  }

  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

  for (const day of days) {
    const hoursOfDay = list.filter((h) => h.selectedDays[day]);
    const ordered = hoursOfDay
      .map((h) => ({
        ...h,
        open: parseHour(h.openHour),
        close: parseHour(h.closeHour),
      }))
      .sort((a, b) => a.open - b.open);

    for (let i = 1; i < ordered.length; i++) {
      const previous = ordered[i - 1];
      const current = ordered[i];

      if (current.open < previous.close) {
        return `${t('sobreposicaoHorario')} ${t(`diasDaSemana.${mappedDays[day]}`)}: ${previous.openHour}-${previous.closeHour} ${t('e')} ${current.openHour}-${current.closeHour}`;
      }
    }
  }
  return null;
}

function parseHour(hora: string): number {
  const [h, m] = hora.split(':').map(Number);
  return h * 60 + m;
}

const mappedDays = {
  sun: 'dom',
  mon: 'seg',
  tue: 'ter',
  wed: 'qua',
  thu: 'qui',
  fri: 'sex',
  sat: 'sab',
};
